import * as React from 'react';
import { AllHTMLAttributes, HTMLProps } from 'react';
import classNames from 'classnames';
import { compose, branch, mapProps } from 'recompose';
import { omit } from 'lodash';

import {
  WrappedFieldInputProps,
  WrappedFieldProps,
  change as reduxFormChange,
  untouch as reduxFormUntouch,
} from 'redux-form';
import { connect } from 'react-redux';

import SvgIcon from '../../SvgIcon';
import FormLabel from '../FormLabel';
import FormError from '../FormError';
import { IFormInput } from '../FormInput';

import IMPComponent from '../../interfaces/IMPComponent';

import './styles.scss';

export interface IFormFieldGeneralProps extends IMPComponent, AllHTMLAttributes<HTMLInputElement> {
  label: string;
  withErrorBelow?: boolean;
  extraActionContent?: React.ReactNode;
  extraActionNodeProps?: HTMLProps<HTMLButtonElement>;
}

interface IFormFieldProps extends IFormFieldGeneralProps, WrappedFieldProps {
  InputComponent: React.ComponentType<WrappedFieldInputProps & IFormInput>;
}

export interface IClearableProps {
  clearable?: boolean;
}

const FormField: React.FunctionComponent<IFormFieldProps> = ({
  className,
  style,
  label,
  withErrorBelow,
  InputComponent,
  input,
  meta,
  extraActionContent,
  extraActionNodeProps,
  ...rest
}) => {
  const cn = classNames('FormField', className);
  const hasError = meta.touched && meta.error;
  const canShowError = withErrorBelow && hasError;
  const props = omit(rest, ['changeFormField', 'untouchFormField']);

  return (
    <div className={cn} style={style}>
      <div className="FormField-label">
        <FormLabel>{label}</FormLabel>
      </div>
      <div className="FormField-input">
        <InputComponent error={hasError} {...input} {...props} />
        {extraActionContent && (
          <button tabIndex={-1} type={'button' as any} className="FormField-action" {...extraActionNodeProps}>
            {extraActionContent}
          </button>
        )}
      </div>
      {canShowError && (
        <div className="FormField-error">
          <FormError>{meta.error}</FormError>
        </div>
      )}
    </div>
  );
};

FormField.defaultProps = {
  withErrorBelow: false,
  extraActionContent: null,
  extraActionNodeProps: {},
};

const enhance = compose<IFormFieldGeneralProps & IClearableProps, IFormFieldProps>(
  connect(
    null,
    {
      changeFormField: reduxFormChange,
      untouchFormField: reduxFormUntouch,
    },
  ),
  branch(
    ({ clearable }) => Boolean(clearable),
    mapProps(({ changeFormField, untouchFormField, clearable, ...rest }: any) => {
      const {
        meta: { form },
        input: { name },
      } = rest;

      return {
        ...rest,
        extraActionContent: <SvgIcon iconName="erase" />,
        extraActionNodeProps: {
          onClick: () => {
            changeFormField(form, name, null);
            untouchFormField(form, name);
          },
        },
      };
    }),
  ),
);

export default enhance(FormField);



// WEBPACK FOOTER //
// ./src/components/Form/FormField/index.tsx